import type { MemberRole } from './access'
import { isTripProductDay, tripClockParts } from './tripTime'

export type ChallengeKind = 'trip_secret' | 'daily'
export type ChallengeCategory =
  | 'humor'
  | 'observation_photo'
  | 'culture'
  | 'gastronomy'
  | 'mini_adventure'
  | 'social_interaction'
export type ChallengeDayPhase = 'locked' | 'active' | 'group_revealed'

export type ChallengeInput = {
  tripId: string
  kind: ChallengeKind
  productDay: string | null
  title: string
  detail: string
  category: ChallengeCategory
  safetyNote: string
}

export type ChallengeAssignmentInput = { challengeId: string; memberId: string }

const categories = new Set<ChallengeCategory>([
  'humor', 'observation_photo', 'culture', 'gastronomy', 'mini_adventure', 'social_interaction',
])

export function challengeDayPhase(productDay: string, now = new Date()): ChallengeDayPhase {
  const clock = tripClockParts(now)
  if (clock.date < productDay || (clock.date === productDay && clock.hour * 60 + clock.minute < 120)) return 'locked'
  if (clock.date > productDay || (clock.date === productDay && clock.hour * 60 + clock.minute >= 1320)) return 'group_revealed'
  return 'active'
}

export function validateChallengeInput(value: ChallengeInput | Record<string, unknown>, role: MemberRole): ChallengeInput {
  if (role !== 'admin') throw new Error('Seul l’administrateur peut préparer un défi.')
  const record = value as Record<string, unknown>
  if (['score', 'points', 'ranking', 'leaderboard'].some((key) => key in record)) {
    throw new Error('Les défis Caravan ne comportent ni score ni classement.')
  }
  const title = typeof record.title === 'string' ? record.title.trim() : ''
  const detail = typeof record.detail === 'string' ? record.detail.trim() : ''
  const safetyNote = typeof record.safetyNote === 'string' ? record.safetyNote.trim() : ''
  const category = record.category as ChallengeCategory
  const kind = record.kind as ChallengeKind
  const productDay = typeof record.productDay === 'string' ? record.productDay : null
  if (typeof record.tripId !== 'string' || !['trip_secret', 'daily'].includes(kind) || !categories.has(category)) {
    throw new Error('Défi invalide.')
  }
  if (title.length < 3 || title.length > 120 || detail.length > 500 || safetyNote.length > 500) {
    throw new Error('Le titre ou le détail du défi est invalide.')
  }
  if (kind === 'daily' && !/^\d{4}-\d{2}-\d{2}$/u.test(productDay ?? '')) {
    throw new Error('Le jour du défi quotidien est requis.')
  }
  if (kind === 'daily' && !isTripProductDay(productDay ?? '')) {
    throw new Error('Le jour du défi doit rester dans les dates du voyage.')
  }
  if (category === 'social_interaction') {
    const normalized = safetyNote.toLocaleLowerCase('fr-FR')
    const hasRespect = normalized.includes('respect')
    const hasAgreement = /accord|consent/u.test(normalized)
    const hasSafety = /sûr|sécur|lieu public/u.test(normalized)
    if (!hasRespect || !hasAgreement || !hasSafety) {
      throw new Error('Une interaction doit imposer respect, accord explicite et sécurité.')
    }
  }
  return {
    tripId: record.tripId,
    kind,
    productDay: kind === 'daily' ? productDay : null,
    title,
    detail,
    category,
    safetyNote,
  }
}

export function validateDistinctAssignments(
  assignments: ChallengeAssignmentInput[],
  expectedMemberIds: string[],
): ChallengeAssignmentInput[] {
  if (assignments.length !== 6 || expectedMemberIds.length !== 6) {
    throw new Error('L’attribution exige exactement six membres et six défis.')
  }
  const members = new Set(assignments.map((item) => item.memberId))
  const challenges = new Set(assignments.map((item) => item.challengeId))
  if (members.size !== 6 || challenges.size !== 6 || expectedMemberIds.some((id) => !members.has(id))) {
    throw new Error('Chaque membre doit recevoir un défi distinct.')
  }
  return assignments.map((item) => ({ ...item }))
}
